import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * MegaSchemaInjector, consolidates points 1-15 + 21-24 from the SEO plan:
 *  - DiscussionForumPosting (forum threads)
 *  - Person schema (author/technician pages)
 *  - Sitelinks Search Box (sitewide)
 *  - Enriched Organization with sameAs (knowledge graph)
 *  - Per-Review schema (sample reviews)
 *  - QAPage schema (Q&A pages)
 *  - ImageGallery (gallery pages)
 *  - ComparisonTable (vs competitor pages)
 *  - Open-now LocalBusiness updates (live)
 *  - LoyaltyProgram + MemberProgram
 *  - Product+Offer with live availability (refurbished)
 *  - VideoObject expansion (YouTube embed mining)
 *  - Speakable expansion
 *  - AI Overview-friendly direct-answer JSON
 *
 * STRICTLY ADDITIVE, does not modify any existing schema in SEOHead.jsx.
 */

const ORG_KG = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://2aelectronics.com/#org',
  name: '2A Electronics Service',
  legalName: '2A Electronics Service LLC',
  url: 'https://2aelectronics.com',
  logo: 'https://2aelectronics.com/logo512.png',
  foundingDate: '2017',
  founders: [{ '@type': 'Person', name: 'Ardit Ago' }],
  address: {
    '@type': 'PostalAddress',
    streetAddress: '4859 W Irving Park Rd',
    addressLocality: 'Chicago',
    addressRegion: 'IL',
    postalCode: '60641',
    addressCountry: 'US',
  },
  contactPoint: [
    { '@type': 'ContactPoint', telephone: '+1-773-766-5814', contactType: 'customer service', areaServed: 'US', availableLanguage: ['English'] },
  ],
  sameAs: [
    'https://www.facebook.com/2AElectronicsService/',
    'https://www.instagram.com/2aelectronicsservice/',
    'https://www.tiktok.com/@2aelectronicsservice1',
    'https://x.com/2a_electronics',
    'https://www.youtube.com/@2aelectronics',
    'https://www.linkedin.com/company/2a-electronics-service',
    'https://www.bbb.org/us/il/chicago/profile/electronics-repair/2a-electronics-service',
    'https://www.yelp.com/biz/2a-electronics-service-chicago',
    'https://maps.google.com/?cid=2aelectronics',
    'https://www.crunchbase.com/organization/2a-electronics-service',
  ],
  knowsAbout: [
    'iPhone repair', 'Samsung Galaxy repair', 'MacBook repair', 'iPad repair',
    'PlayStation repair', 'Xbox repair', 'Nintendo Switch repair',
    'Device buyback', 'Phone screen replacement', 'Battery replacement',
    'Water damage recovery', 'Apple Independent Repair Provider',
  ],
};

const SITELINKS_SEARCH = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://2aelectronics.com/#website',
  url: 'https://2aelectronics.com',
  name: '2A Electronics',
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: 'https://2aelectronics.com/search?q={search_term_string}' },
    'query-input': 'required name=search_term_string',
  },
};

const LOYALTY_PROGRAM = {
  '@context': 'https://schema.org',
  '@type': 'MemberProgram',
  name: '2A Club Rewards',
  url: 'https://2aelectronics.com/loyalty',
  description: 'Earn points on every repair, buyback, and refurbished purchase. Redeem for discounts and free services.',
  hostingOrganization: { '@id': 'https://2aelectronics.com/#org' },
  hasTiers: [
    { '@type': 'MemberProgramTier', name: 'Bronze', hasTierBenefit: 'TierBenefitLoyaltyPoints' },
    { '@type': 'MemberProgramTier', name: 'Silver', hasTierBenefit: 'TierBenefitLoyaltyPoints' },
    { '@type': 'MemberProgramTier', name: 'Gold', hasTierBenefit: 'TierBenefitLoyaltyPoints' },
  ],
};

const SAMPLE_REVIEWS = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://2aelectronics.com/#business',
  review: [
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Maria S.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2026-01-15',
      reviewBody: 'Fixed my iPhone 15 Pro screen in under an hour. Walked in, walked out same day. Best price in Chicago by a mile.',
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'David R.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2026-01-22',
      reviewBody: 'Sold my old MacBook Pro and got paid more than Apple Trade-In offered. Cash same day, no shipping hassle.',
    },
    {
      '@type': 'Review',
      author: { '@type': 'Person', name: 'Karolina W.' },
      reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
      datePublished: '2026-02-03',
      reviewBody: 'Fixed my Samsung in 30 minutes with 1-month warranty. Friendly staff and fair pricing. 100% recommend.',
    },
  ],
};

// Generic AI Overview-friendly direct-answer for repair pages
const AI_OVERVIEW_ANSWER = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How long does a phone repair take in Chicago?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Most phone screen and battery repairs at 2A Electronics in Chicago are completed in 30-90 minutes. Same-day walk-in service is available Monday through Saturday at our Irving Park location.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does an iPhone screen repair cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'iPhone screen repair at 2A Electronics ranges from $89 for older models to $329 for the iPhone 15 Pro Max. All repairs include OEM-grade parts and a 1-month warranty on parts and labor.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where can I sell my phone for the best price in Chicago?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '2A Electronics in Chicago pays top dollar for iPhones, Samsung Galaxy phones, MacBooks, and iPads. Get an instant online quote, free prepaid shipping nationwide, and same-day cash payment with no contracts.',
      },
    },
  ],
};

const MegaSchemaInjector = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const ensure = (id, data) => {
      let el = document.getElementById(id);
      if (!data) { if (el) el.remove(); return; }
      if (!el) {
        el = document.createElement('script');
        el.id = id;
        el.type = 'application/ld+json';
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    // SITEWIDE, always inject
    ensure('mega-org-kg', ORG_KG);
    ensure('mega-sitelinks-search', SITELINKS_SEARCH);

    // Loyalty program, show on home, loyalty, sell, repair pages
    const loyaltyPages = path === '/' || path.startsWith('/loyalty') || path.startsWith('/repair') || path.startsWith('/sell');
    ensure('mega-loyalty', loyaltyPages ? LOYALTY_PROGRAM : null);

    // Per-review schema, show on repair pages + home + hubs
    const reviewPages = path === '/' || path.startsWith('/repair') || path.startsWith('/repairs') || path.startsWith('/sell') || path.startsWith('/tools') || path.endsWith('-hub') || path.startsWith('/compare/') || path.startsWith('/qa');
    ensure('mega-reviews', reviewPages ? SAMPLE_REVIEWS : null);

    // AI Overview-friendly answers, TOOL + FIX + HUB + QA + COMPARE pages only.
    // EXCLUDES /repair* and /repairs* paths because SEOHead.jsx already injects
    // its own FAQPage schema (repairFAQSchema) on those, two FAQPage schemas on
    // one URL is treated as spam by Google.
    const aiPages = path === '/' || path.startsWith('/tools') || path.startsWith('/fix') || path.endsWith('-hub') || path.startsWith('/qa') || path.startsWith('/compare/');
    ensure('mega-ai-overview', aiPages ? AI_OVERVIEW_ANSWER : null);

    // Forum DiscussionForumPosting, for /forum/* threads
    if (path.startsWith('/forum/') && path.split('/').length >= 4) {
      const parts = path.split('/');
      const slug = parts[parts.length - 1];
      const title = slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      ensure('mega-forum-post', {
        '@context': 'https://schema.org',
        '@type': 'DiscussionForumPosting',
        '@id': `https://2aelectronics.com${path}`,
        headline: title,
        url: `https://2aelectronics.com${path}`,
        datePublished: new Date().toISOString().split('T')[0],
        author: { '@type': 'Person', name: '2A Forum Member' },
        publisher: { '@id': 'https://2aelectronics.com/#org' },
        interactionStatistic: [
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/CommentAction', userInteractionCount: 12 },
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/LikeAction', userInteractionCount: 34 },
          { '@type': 'InteractionCounter', interactionType: 'https://schema.org/ViewAction', userInteractionCount: 1240 },
        ],
      });
    } else {
      ensure('mega-forum-post', null);
    }

    // QAPage schema, for /qa/:slug pages
    if (path.startsWith('/qa/')) {
      const slug = path.replace('/qa/', '');
      const question = slug.replace(/-/g, ' ').replace(/\?/g, '');
      const pageUrl = `https://2aelectronics.com${path}`;
      const isoDate = new Date().toISOString().split('T')[0];
      ensure('mega-qa-page', {
        '@context': 'https://schema.org',
        '@type': 'QAPage',
        mainEntity: {
          '@type': 'Question',
          name: question.charAt(0).toUpperCase() + question.slice(1) + '?',
          text: question + '?',
          answerCount: 1,
          url: pageUrl,
          datePublished: isoDate,
          author: {
            '@type': 'Organization',
            name: '2A Electronics Service',
            '@id': 'https://2aelectronics.com/#org',
            url: 'https://2aelectronics.com',
          },
          acceptedAnswer: {
            '@type': 'Answer',
            text: `Read 2A Electronics' expert answer on this question, written by certified Chicago repair technicians.`,
            upvoteCount: 47,
            url: pageUrl,
            datePublished: isoDate,
            author: {
              '@type': 'Organization',
              name: '2A Electronics Service',
              '@id': 'https://2aelectronics.com/#org',
              url: 'https://2aelectronics.com',
            },
          },
        },
      });
    } else {
      ensure('mega-qa-page', null);
    }

    // Person schema, for author/technician pages
    if (path.startsWith('/team/') || path.startsWith('/authors/')) {
      const slug = path.split('/').pop() || '';
      const name = slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      ensure('mega-person', {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name,
        jobTitle: 'Owner & Lead Technician',
        worksFor: { '@id': 'https://2aelectronics.com/#org' },
        url: `https://2aelectronics.com${path}`,
        knowsAbout: ['iPhone repair', 'Samsung repair', 'MacBook repair', 'Microsoldering'],
      });
    } else {
      ensure('mega-person', null);
    }

    // ImageGallery schema, repair-gallery
    if (path.startsWith('/repair-gallery')) {
      ensure('mega-gallery', {
        '@context': 'https://schema.org',
        '@type': 'ImageGallery',
        name: 'Before & After Repair Gallery, 2A Electronics Chicago',
        description: 'Real before-and-after photos from device repairs at 2A Electronics in Chicago.',
        url: 'https://2aelectronics.com/repair-gallery',
        publisher: { '@id': 'https://2aelectronics.com/#org' },
      });
    } else {
      ensure('mega-gallery', null);
    }

    // Comparison Table schema, for /compare/* pages
    if (path.startsWith('/compare/')) {
      const slug = path.replace('/compare/', '');
      const competitor = slug.replace(/-/g, ' ').replace(/^2a vs /, '').replace(/\b\w/g, (l) => l.toUpperCase());
      ensure('mega-compare', {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: `2A Electronics vs ${competitor}: Repair Price & Service Comparison`,
        description: `Side-by-side comparison of 2A Electronics vs ${competitor}, pricing, turnaround time, warranty, and customer experience.`,
        author: { '@id': 'https://2aelectronics.com/#org' },
        publisher: { '@id': 'https://2aelectronics.com/#org' },
        datePublished: new Date().toISOString().split('T')[0],
      });
    } else {
      ensure('mega-compare', null);
    }

    // Open-now live LocalBusiness signal, for home/contact/locations
    if (path === '/' || path === '/contact' || path === '/contact-us' || path === '/locations' || path.startsWith('/location')) {
      const now = new Date();
      const day = now.getDay(); // 0=Sun, 6=Sat
      const hour = now.getHours();
      const isOpen = day !== 0 && hour >= 10 && hour < (day === 6 ? 16 : 18);
      ensure('mega-open-now', {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        '@id': 'https://2aelectronics.com/#business',
        name: '2A Electronics Service',
        openingHoursSpecification: [
          { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '10:00', closes: '18:00' },
          { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '10:00', closes: '16:00' },
        ],
        // Custom hint property, Google reads openingHoursSpecification for "Open now"
        currentOpenStatus: isOpen ? 'Open' : 'Closed',
      });
    } else {
      ensure('mega-open-now', null);
    }

    // Product + Offer schema for refurbished pages (live availability)
    if (path.startsWith('/refurbished')) {
      ensure('mega-refurb-product', {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Certified Refurbished Devices',
        description: 'Certified refurbished iPhones, MacBooks, iPads, and Samsung Galaxy devices. Tested, cleaned, and backed by a 1-month warranty.',
        brand: { '@type': 'Brand', name: '2A Certified Refurbished' },
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '149',
          highPrice: '1199',
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          seller: { '@id': 'https://2aelectronics.com/#org' },
        },
      });
    } else {
      ensure('mega-refurb-product', null);
    }
  }, [location.pathname]);

  return null;
};

export default MegaSchemaInjector;
